import './RagChat.scss';

import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Badge, Breadcrumb, Button, Col, Container, Form, ProgressBar, Row } from 'react-bootstrap'
import { Link, useParams } from 'react-router-dom'
import { ChatMessage, EmbeddedLoader, Rag, RagConfigs, RagLoader } from '../../../models/LLM';
import { EmbedBody, Llm } from '../../../api/Llm';
import { useToasts } from '../../../contexts/toasts';
import IconButton from '../../../components/common/IconButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { regular, solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import MessageCloud from '../../../components/generics/MessageCloud';
import ChatInput from '../../../components/generics/ChatInput';
import TypingLoader from '../../../components/generics/TypingLoader';
import { Flex, Modal, Select, Tabs, TabsProps, Tooltip } from 'antd';
import EditLoaderModal from '../../../components/generics/EditLoaderModal';
import { useFullScreenLoader } from '../../../contexts/fullScreenLoader';
import classNames from 'classnames';
import { cloneDeep, set } from 'lodash';
import { formatDate } from '../../../utils';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { sectionLabelsSelector } from '../../../slices/sectionsSlice';
import { Helmet } from 'react-helmet';
import AppLoader from '../../../components/common/AppLoader';
import { v4 } from 'uuid';
import { EventEmitter } from '../../../utils/EventEmitter';
import { AppNotification } from '../../../models/Notification';

function RagChat() {
  const { id } = useParams();
  const [showSettings, setShowSettings] = useState(true);
  const [prompt, setPrompt] = useState('');
  const [error, setError] = useState(false);
  const [rag, setRag] = useState<Rag>();
  const [loaders, setLoaders] = useState<RagLoader[]>();
  const [ragConfigs, setRagConfigs] = useState<RagConfigs>();
  const [showCreateLoaderModal, setShowCreateLoaderModal] = useState(false);
  const [showEmbedLoaderModal, setShowEmbedLoaderModal] = useState(false);
  const [history, setHistory] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [loader, setLoader] = useState<RagLoader>(new RagLoader());
  const [usedLoaders, setUsedLoaders] = useState<number[]>([]);

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();

  const ragLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "rags"));
  const loaderLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "loaders"));
  const apiKeyLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "api-keys"));

  const settingsTabs: TabsProps['items'] = useMemo(() => ([
    { key: 'loaders', label: `Manage ${loaderLabels.plural || "Loaders"}`, icon: <FontAwesomeIcon icon={solid('bars-progress')} /> },
    { key: 'context', label: 'Context Configuration', icon: <FontAwesomeIcon icon={solid('tools')} /> },
    { key: 'api-keys', label: apiKeyLabels.plural || "API Keys", icon: <FontAwesomeIcon icon={solid('key')} /> },
  ]), [loaderLabels, apiKeyLabels]);

  const fixedContext = useMemo(() => {
    return rag && ragConfigs && Object.entries(rag.context).reduce((acc, [key, value]) => {
      acc[key] = value !== undefined ? value : ragConfigs.configs.find(c => c.name === key)?.default;
      return acc;
    }, {} as Record<string, any>)
  }, [rag, ragConfigs]);

  useEffect(() => {
    if (id && rag) {
      EventEmitter.subscribe("JOB_PROGRESS", (newNotification: AppNotification) => {
        if (newNotification.details.resourceId === +id) {
          setRag(r => {
            const newRag = cloneDeep(r!);
            const found = r!.loaders!.findIndex(l => l.jobId === newNotification.details.jobId);
            if (found < 0) return newRag;
            newRag.loaders![found].status = {
              progress: newNotification.details.progress,
              total: newNotification.details.total,
              finished: newNotification.details.finished,
            }
            return newRag;
          })
        }
      });

      return () => EventEmitter.unsubscribe("JOB_PROGRESS");
    }
  }, [id, rag]);

  const initRag = useCallback(() => {
    Llm.getRag(id!)
      .then(
        ({ data }) => {
          setRag(data);
          setUsedLoaders(data.loaders?.map(l => l.id!) || []);
        },
        (err) => {
          setError(true);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('rags-error', message, `${ragLabels.plural} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
    // eslint-disable-next-line
  }, [id, showToast]);

  const initRagContextConfigs = useCallback(() => {
    Llm.getRagsContextConfigs()
      .then(
        ({ data }) => {
          const fixedConfigs = cloneDeep(data);
          fixedConfigs.configs.forEach(c => {
            c.value = rag!.context[c.name];
          });
          setRagConfigs(fixedConfigs);
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('rag-error', message, `${ragLabels.singular} Configs Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
    // eslint-disable-next-line
  }, [rag, showToast]);

  const getLoaders = useCallback(() => {
    const params = new URLSearchParams();
    if (usedLoaders.length) params.set('excludeIds', usedLoaders.join(','));

    Llm.getLoaders(params)
      .then(
        ({ data }) => setLoaders((data as Array<RagLoader>)),
        (err) => {
          setLoaders([]);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('loaders-error', message, `${loaderLabels.plural} Error ${status ? `(${status})` : ''}`, 'danger');
        });
    // eslint-disable-next-line
  }, [showToast, usedLoaders]);

  useEffect(getLoaders, [getLoaders]);
  useEffect(initRag, [initRag]);

  useEffect(() => {
    if (rag && !ragConfigs) initRagContextConfigs();
  }, [rag, ragConfigs, initRagContextConfigs]);

  const scrollChatToBottom = useCallback(() => {
    const chatContainer = document.getElementById('chat-history-container');
    if (chatContainer) chatContainer.scrollTop = chatContainer.scrollHeight;
  }, []);

  useEffect(scrollChatToBottom, [history, scrollChatToBottom]);

  const reloadLoaders = useCallback(() => {
    setLoaders(undefined);
    getLoaders();
  }, [getLoaders]);

  const handleClose = useCallback(() => {
    setShowEmbedLoaderModal(false);
    setShowCreateLoaderModal(false);
    setLoader(new RagLoader());
  }, []);

  const embedLoader = useCallback((loader: RagLoader) => () => {
    showLoader();

    const body: EmbedBody = {
      jobId: v4(),
      ragId: +id!,
      loaderId: loader.id!,
      context: fixedContext!,
      loader: {
        name: loader.type!,
        config: loader.config!.reduce<Record<string, any>>((acc, curr) => {
          if (curr.value !== undefined) acc[curr.name] = curr.value;
          return acc;
        }, {}),
      },
      splittingConf: { "chunk_size": 1000, "chunk_overlap": 200 },
      accessLevel: rag!.accessLevel,
      organizationId: rag!.organization.id,
      ownerId: rag!.owner.id,
    };

    Llm.embedRagLoader(body)
      .then(
        () => {
          hideLoader();
          showToast('loader-embed-success', `${loaderLabels.singular} embedded successfully`, `${loaderLabels.singular} Success`, 'success');
          handleClose();
          initRag();
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('loader-embed-error', message, `${loaderLabels.singular} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [id, rag, fixedContext, showLoader, showToast, hideLoader, handleClose, initRag, loaderLabels]);

  const invokeRag = useCallback((question: string) => {
    setLoading(true);

    const body = {
      context: fixedContext!,
      input: { question },
      thread_id: `${rag!.chats![0].id}`,
    }

    Llm.invokeRag(id!, body)
      .then(
        ({ data }) => {
          setLoading(false);
          const systemMessage: ChatMessage = {
            isUserPrompt: false,
            timestamp: new Date().toISOString(),
            text: data.answer.content,
          };
          setHistory(h => h!.concat(systemMessage));
        },
        (err) => {
          setLoading(false);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('rag-invoke-error', message, `Invoke Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [id, rag, showToast, fixedContext]);

  const handleSendMessage = useCallback(() => {
    const newMessage: ChatMessage = {
      timestamp: new Date().toISOString(),
      text: prompt,
      isUserPrompt: true,
    };
    setHistory(h => h!.concat(newMessage));
    setPrompt('');
    invokeRag(prompt);
  }, [prompt, invokeRag]);

  const handleFileDialog = useCallback(() => {
    document.getElementById('file-upload-input')?.click();
  }, []);

  const handlePromptChange = useCallback((evt: React.ChangeEvent<HTMLInputElement>) => {
    setPrompt(evt.target.value);
  }, []);

  const toggleSettings = useCallback(() => setShowSettings(s => !s), []);
  const openCreateLoaderModal = useCallback(() => setShowCreateLoaderModal(true), []);
  const openEmbedLoaderModal = useCallback(() => setShowEmbedLoaderModal(true), []);

  const saveContextConfigs = useCallback(() => {
    showLoader();

    const body = ragConfigs!.configs.reduce((acc, curr) => {
      acc[curr.name] = curr.value || curr.default;
      return acc;
    }, {} as Record<string, any>);

    Llm.updateRagContext(id!, body)
      .then(
        ({ data }) => {
          hideLoader();
          showToast('rag-update-success', `${ragLabels.singular} context updated successfully`, `${ragLabels.singular} Success`, 'success');
          setRag(data);
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('rag-invoke-error', message, `Invoke Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [id, showToast, showLoader, hideLoader, ragConfigs, ragLabels]);

  const handleContextChange = useCallback((path: string) => (evt: any) => {
    const value = evt.target?.value || evt;

    setRagConfigs(curr => {
      const newConfigs = cloneDeep(curr!);
      set(newConfigs!, path, value);
      return newConfigs;
    });
  }, []);

  const handleSubmit = useCallback((loader: RagLoader) => {
    showLoader();

    Llm.createLoader(loader)
      .then(
        () => {
          hideLoader();
          showToast('loader-success', `${loaderLabels.singular} created successfully`, `${loaderLabels.singular} Success`, 'success');
          handleClose();
          reloadLoaders();
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('loaders-error', message, `${loaderLabels.plural} Error ${status ? `(${status})` : ''}`, 'danger');
        });
  }, [showLoader, hideLoader, showToast, reloadLoaders, handleClose, loaderLabels]);

  const renderSettingsTab = useCallback((key: string) => {
    if (key === 'loaders') return (
      <div className="tab-flex-content">
        <Flex className="p-2 border-bottom">
          {!!rag?.loaders?.length && (
            <Button size="sm" variant="light" className="border" onClick={openEmbedLoaderModal}>
              <FontAwesomeIcon icon={solid('plus-circle')} />
              <span className="ms-2">Embed existing {loaderLabels.singular}</span>
            </Button>
          )}

          <Button size="sm" variant="success" className="ms-auto" onClick={openCreateLoaderModal}>
            <FontAwesomeIcon icon={solid('plus')} />
            <span className="ms-2">Create new {loaderLabels.singular}</span>
          </Button>
        </Flex>

        <div className="tab-scroll-content">
          {rag?.loaders?.length === 0 && (
            <button className="add-loaders-alert" onClick={openEmbedLoaderModal}>
              <FontAwesomeIcon icon={solid('plus-circle')} size="lg" />
              <span className="mt-2">Add {loaderLabels.singular}</span>
            </button>
          )}

          {rag?.loaders?.map(loader => <LoaderItem key={loader.id} loader={loader} embedded />)}
        </div>
      </div>
    )

    if (key === 'context') return (
      <div className="tab-flex-content">
        <Flex className="p-2 border-bottom">
          <Button variant="success" size="sm" className="ms-auto" onClick={saveContextConfigs}>
            <FontAwesomeIcon icon={solid('save')} />
            <span className="ms-2">Save Configuration</span>
          </Button>
        </Flex>

        <div className="tab-scroll-content">
          <p className="text-muted">{ragConfigs?.description}</p>

          {ragConfigs?.configs.map((config, index) => (
            <Form.Group key={index} controlId={`${config.name}-group`} className="mb-2">
              <Form.Label>{config.name}</Form.Label>
              {config.type === 'boolean'
                ? <Form.Check checked={config.value as boolean} onChange={handleContextChange(`config.[${index}].value`)} />
                : config.type === 'array'
                  ? <Select mode="tags" placeholder={config.default ? `${config.default}` : config.type} value={config.value as string[] || []} onChange={handleContextChange(`configs.[${index}].value`)} />
                  : <Form.Control size="sm" placeholder={config.default ? `${config.default}` : config.type} value={config.value as string || ""} onChange={handleContextChange(`configs.[${index}].value`)} />
              }
              <Form.Text>{config.description}</Form.Text>
            </Form.Group>
          ))}
        </div>
      </div>
    )
  }, [rag, ragConfigs, handleContextChange, openCreateLoaderModal, openEmbedLoaderModal, saveContextConfigs, loaderLabels]);

  const renderChatToolbar = useCallback(() => {
    return (
      <Row className="chat-toolbar mx-0">
        <Col>
          <ChatInput
            prompt={prompt}
            onPromptChange={handlePromptChange}
            onSendMessage={handleSendMessage}
          />
          <input className="d-none" type="file" id="file-upload-input" />
        </Col>
        <Col className="buttons-col">
          <IconButton tooltip="Attache Files" onClick={handleFileDialog}>
            <FontAwesomeIcon icon={solid('paperclip')} />
          </IconButton>
          <IconButton className="ms-3" tooltip={(showSettings ? "Hide" : "Show") + " Settings"} onClick={toggleSettings}>
            <FontAwesomeIcon icon={showSettings ? solid('arrow-right') : solid('cog')} />
          </IconButton>
        </Col>
      </Row>
    )
  }, [prompt, showSettings, toggleSettings, handlePromptChange, handleFileDialog, handleSendMessage]);

  const renderChat = useCallback(() => {
    return (
      <div className="llm-chat-container">
        {rag!.loaders?.length === 0 && (
          <Alert variant="info" className="py-2 mb-2">
            <FontAwesomeIcon icon={solid('info-circle')} />
            <span className="ms-2">No {loaderLabels.plural} found for this chat. The AI Bot will not be able to answer to specific topics without them.</span>
          </Alert>
        )}

        <div className="chat-history" id="chat-history-container">
          {history.length === 0 && (
            <Flex justify="center" className="py-5">
              <div className="empty-chat-alert">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={0.75} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 0 1-.825-.242m9.345-8.334a2.126 2.126 0 0 0-.476-.095 48.64 48.64 0 0 0-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0 0 11.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155" />
                </svg>

                <p className="text-center mt-3">
                  Welcome to your brand new chat, start exchanging messages now!
                </p>
              </div>
            </Flex>
          )}

          {history.map(message => (
            <Row className="mx-0 py-1" key={message.timestamp}>
              <MessageCloud message={message} />
            </Row>
          ))}

          {loading && (
            <Row className="mx-0 py-1">
              <MessageCloud>
                <TypingLoader />
              </MessageCloud>
            </Row>
          )}
        </div>

        {renderChatToolbar()}
      </div>
    )
  }, [rag, loading, history, renderChatToolbar, loaderLabels]);

  const renderAsyncContent = useCallback(() => {
    if (error) return (
      <Alert variant="danger" className="py-2">
        <FontAwesomeIcon icon={solid('exclamation-triangle')} />
        <span className="ms-2">An error occurred. Try again later.</span>
      </Alert>
    )

    if (!rag) return <AppLoader className="py-5 my-5" justify="center" size={100} />;

    return (
      <Row className="rag-chat-row">
        <Col xs={showSettings ? 9 : 12}>{renderChat()}</Col>

        <Col xs={3} className={classNames("settings-col", { show: showSettings })}>
          <div className="settings-container">
            <Tabs
              defaultActiveKey="loaders"
              items={
                settingsTabs!.map(tab => ({
                  ...tab,
                  children: renderSettingsTab(tab.key),
                }))
              }
            />
          </div>
        </Col>
      </Row>
    )
  }, [rag, error, showSettings, renderChat, renderSettingsTab, settingsTabs]);

  return (
    <Container fluid className="rag-chat mt-3">
      <Helmet>
        <title>Rag {id} Chat</title>
      </Helmet>

      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/llm/rags' }}>{ragLabels.plural} List</Breadcrumb.Item>
        {rag && <Breadcrumb.Item active>'{rag.name}' Chat</Breadcrumb.Item>}
      </Breadcrumb>

      {renderAsyncContent()}

      <EditLoaderModal loader={loader} open={showCreateLoaderModal} onClose={handleClose} onSubmit={handleSubmit} />

      <Modal
        open={showEmbedLoaderModal}
        onCancel={handleClose}
        onClose={handleClose}
        footer={null}
      >
        <h6>Embed {loaderLabels.singular}</h6>

        <br />

        {loaders?.map(loader => (
          <LoaderItem
            key={loader.id}
            loader={loader}
            onEmbed={embedLoader(loader)}
          />
        ))}
      </Modal>
    </Container>
  )
}

type Props = { onEmbed?: (loader: RagLoader) => void } & ({
  embedded?: false;
  loader: RagLoader;
} | {
  embedded: true;
  loader: EmbeddedLoader;
})

const LoaderItem = memo(({ loader, onEmbed, embedded }: Props) => {
  const loaderLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "loaders"));
  // -1 means loading
  const loaderPercent = useMemo(() => {
    if (!embedded) return;

    if (!loader.status || !loader.status.progress || !loader.status.total) return -1;

    return Math.floor(loader.status.progress * 100 / loader.status.total);
  }, [loader, embedded]);

  const handleEmbedLoader = useCallback(() => onEmbed?.(loader), [loader, onEmbed]);

  const renderStatus = useCallback(() => {
    if (!embedded || loaderPercent === undefined) return;

    if (loaderPercent === 100 || loader.status?.finished) return (
      <Tooltip title="Completed">
        <FontAwesomeIcon icon={regular('check-circle')} size="xl" className="text-success" />
      </Tooltip>
    )

    return (
      <Tooltip key={loaderPercent < 0 ? 'loading' : 'progress'} title={loaderPercent < 0 ? 'Loading' : `${loaderPercent}%`}>
        {loaderPercent < 0
          ? <ProgressBar striped animated now={100} variant="secondary" style={{ height: '0.5rem' }} />
          : <ProgressBar now={loaderPercent} variant="primary" style={{ height: '0.5rem' }} />
        }
      </Tooltip>
    )
  }, [embedded, loaderPercent, loader]);

  return (
    <div key={loader.id} className="loader-item border rounded">
      <Col>
        <h6 className="mb-0">{loader.name}</h6>
        <Badge bg="none" className="bg-pink">{loader.type}</Badge>
        <Tooltip title={loader.description}>
          <FontAwesomeIcon icon={solid('info-circle')} className="ms-1 text-muted" />
        </Tooltip>
        {embedded && loader.embedDateTime && <div className="text-muted font-sm mt-1">{formatDate(loader.embedDateTime)}</div>}
      </Col>

      {!embedded && (
        <IconButton onClick={handleEmbedLoader} tooltip={`Add ${loaderLabels.singular}`}>
          <FontAwesomeIcon icon={solid('plus')} size="lg" />
        </IconButton>
      )}

      {embedded && <Col sm={4} className="text-end pe-2">{renderStatus()}</Col>}
    </div>
  )
});

export default memo(RagChat);